<template>
  <LargeModal
    :show="show"
    title="Accept offer"
    subtitle="Toggle the lightswitch on for the offer(s) you want to accept."
    @close="close"
  >
    <template #default>
      <div
        v-if="loading"
        class="flex justify-center"
      >
        <three-dot-fade size="40" />
      </div>
      <div
        v-else
        class="bg-white shadow overflow-hidden sm:rounded-md"
      >
        <ul
          role="list"
          class="divide-y divide-gray-200"
        >
          <li
            v-for="offer in offers"
            :key="offer.id"
          >
            <div class="flex items-center px-4 py-4 sm:px-6">
              <div class="min-w-0 flex-1 flex items-center">
                <div class="flex-shrink-0">
                  <img
                    class="h-12 w-12 rounded-full"
                    :src="offer.guide_profile.user.profile_photo_url"
                    :alt="offer.guide_profile.user.name"
                  >
                </div>
                <div class="min-w-0 flex-1 px-4">
                  <div>
                    <p class="text-sm font-medium text-indigo-600 truncate">
                      {{ offer.guide_profile.user.name }}
                    </p>
                    <p class="mt-2 flex items-center text-sm text-gray-500">
                      <span class="truncate">{{ offer.guide_profile.team.name }}</span>
                    </p>
                  </div>
                </div>
                <div class="flex flex-col min-w-0 flex-1 px-4 text-sm text-font">
                  <span class="inline-flex justify-between items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-pink-100 text-pink-800">
                    <span>Offer:</span>
                    <span>{{ offer.offer.currency.symbol }} {{ offer.offer.amount }}</span>
                  </span>
                </div>
                <Switch
                  v-model="offer.accept"
                  :class="[offer.accept ? 'bg-indigo-600' : 'bg-gray-200', 'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500']"
                >
                  <span class="sr-only">Use setting</span>
                  <span :class="[offer.accept ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200']">
                    <span
                      :class="[offer.accept ? 'opacity-0 ease-out duration-100' : 'opacity-100 ease-in duration-200', 'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity']"
                      aria-hidden="true"
                    >
                      <svg
                        class="h-3 w-3 text-gray-400"
                        fill="none"
                        viewBox="0 0 12 12"
                      >
                        <path
                          d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                    <span
                      :class="[offer.accept ? 'opacity-100 ease-in duration-200' : 'opacity-0 ease-out duration-100', 'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity']"
                      aria-hidden="true"
                    >
                      <svg
                        class="h-3 w-3 text-indigo-600"
                        fill="currentColor"
                        viewBox="0 0 12 12"
                      >
                        <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                      </svg>
                    </span>
                  </span>
                </Switch>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </template>
    <template #footer>
      <div
        v-if="booking"
        class="flex justify-center items-center h-10 w-28"
      >
        <three-dot-fade size="20" />
      </div>
      <CreateButton
        v-else-if="selected.length > 0"
        @click="book"
      >
        Book {{ selected.length }} {{ selected.length > 1 ? 'offers' : 'offer' }}
      </CreateButton>
      <span
        v-else
        class="my-2 text-sm text-font"
      >
        Select offers to book
      </span>
      <CancelButton
        class="ml-3"
        @click="close"
      />
    </template>
  </LargeModal>
</template>

<script setup>

import { computed, ref, watch } from 'vue';
import LargeModal from '@/ComponentsDeprecated/Modal/LargeModal';
import CreateButton from '@/ComponentsDeprecated/Buttons/CreateButton';
import CancelButton from '@/ComponentsDeprecated/Buttons/CancelButton';
import ThreeDotFade from '@/ComponentsDeprecated/Loaders/ThreeDotFade';
import '@vuepic/vue-datepicker/dist/main.css';
import { Switch } from '@headlessui/vue';
import { router as Inertia } from '@inertiajs/core';

const props = defineProps({
  show: {
    type: Boolean,
    required: false,
    default: false,
  },
  engagement: {
    type: Object,
    required: true,
  },
});

const loading = ref(false);
const booking = ref(false);
const offers = ref([]);
const close = () => {
  emit('close');
};

const emit = defineEmits({ close: (_value) => true });

function getOffers() {
  loading.value = true;
  axios
    .get(route('engagement.offers', { engagement: props.engagement.id }))
    .then((response) => {
      offers.value = response.data.map((o) => {
        o.accept = false;

        return o;
      });
    })
    .catch((error) => {
      console.log(error);
    })
    .finally(() => {
      loading.value = false;
    });
}
function book() {
  booking.value = true;
  axios
    .post(route('engagement.book', { engagement: props.engagement.id }), {
      offers: selected.value.map((o) => o.offer.id),
    })
    .then((response) => {
      Inertia.visit(route('engagements.show', { engagement: props.engagement.id }));
    })
    .catch((error) => {
      console.log(error);
      booking.value = false;
    });
}

watch(() => props.show, (newValue) => {
  if (newValue) {
    getOffers();
  }
});

const selected = computed(() => offers.value.filter((offer) => offer.accept === true));

</script>
